import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import "../../style/customers.css";
import "../../style/sales.css";
import "../../style/customerDetails.css";

import SaleIcon from "../../assets/icons/SaleIcon";
import ReturnIcon from "../../assets/icons/ReturnIcon";
import CreditIcon from "../../assets/icons/CreditIcon";
import DebitIcon from "../../assets/icons/DebitIcon";
import { useSelector } from "react-redux";
import { customersAPI } from "../../services";
import { toast } from "react-toastify";

function CustomerDetailsPrint() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { isPartsURL, isAccessoriesURL } = useSelector((state) => state.domain);
  const hasPrintedRef = useRef(false);
  const exportStateRef = useRef(location.state);
  
  // State for fetched data
  const [customer, setCustomer] = useState(location.state?.customer || null);
  const [transactions, setTransactions] = useState(location.state?.transactions || []);
  const [currentBalance, setCurrentBalance] = useState(location.state?.currentBalance || 0);
  const [loading, setLoading] = useState(!location.state?.customer);
  const [dataLoaded, setDataLoaded] = useState(false);

  // Helper function to format balance
  const formatBalance = (balance) => {
    if (balance === null || balance === undefined) return "€0.00";
    const amount = parseFloat(balance);
    if (isNaN(amount)) return "€0.00";
    return `€${amount.toFixed(2)}`;
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "";
    }
  };

  // Helper function to format date and time for header
  const formatDateTime = () => {
    const now = new Date();
    return now.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Fetch customer and transactions data
  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;

      // If we have data from location.state (from export), use it directly
      if (exportStateRef.current?.customer && exportStateRef.current?.transactions) {
        setCustomer(exportStateRef.current.customer);
        setTransactions(exportStateRef.current.transactions);
        setCurrentBalance(exportStateRef.current.currentBalance || 0);
        setLoading(false);
        setDataLoaded(true);
        return;
      }

      // If no state data, fetch everything with all_data=True
      setLoading(true);
      try {
        // Fetch customer detail
        const customerResult = await customersAPI.getCustomerDetail(id);
        if (customerResult.error) {
          toast.error(customerResult.error.message || "Failed to fetch customer details");
          setCustomer(null);
          setLoading(false);
          return;
        }

        const customerData = customerResult.response?.data || customerResult.response;
        setCustomer(customerData);

        // Fetch customer summary for balance
        const summaryResult = await customersAPI.getCustomerDetailsCount(id);
        if (!summaryResult.error) {
          const stats = summaryResult.response?.data || summaryResult.response || {};
          setCurrentBalance(stats.balance || customerData.balance || 0);
        }

        // Fetch all transactions with all_data=True
        const transactionsResult = await customersAPI.getCustomerFinanceJournals(
          id,
          "",
          {},
          true // all_data=True
        );

        if (transactionsResult.error) {
          console.error("Failed to fetch transactions:", transactionsResult.error.message);
          setTransactions([]);
        } else {
          // Transform journal data to transaction format
          let journalData = [];
          if (transactionsResult.response?.data) {
            journalData = Array.isArray(transactionsResult.response.data)
              ? transactionsResult.response.data
              : [];
          } else if (Array.isArray(transactionsResult.response)) {
            journalData = transactionsResult.response;
          }

          const transformedTransactions = journalData.map((journal) => {
            let salesId = journal.sales;
            if (!salesId && journal.note) {
              const match = journal.note.match(/Sales id : (\d+)/);
              if (match) salesId = parseInt(match[1]);
            }

            return {
              id: journal.id,
              customerId: id,
              date: journal.tnx_date,
              invoiceId: journal.tnx_id,
              type: journal.return_id
                ? "Return"
                : journal.is_sales
                ? "Sale"
                : journal.tnx_type === "credit"
                ? "Credit"
                : "Debit",
              amount: parseFloat(journal.amount || 0),
              details: journal.note || journal.title || "No details",
              balance: parseFloat(journal.balance || 0),
              createdAt: journal.created_at || new Date().toISOString(),
              billAmount: parseFloat(journal.bill_amount || 0),
              paidAmount: parseFloat(journal.paid_amount || 0),
              isSales: journal.is_sales || false,
              salesId: salesId,
              returnId: journal.return_id || null,
              tnx_id: journal.tnx_id,
              tnx_date: journal.tnx_date,
              tnx_type: journal.tnx_type,
              note: journal.note || journal.title || "",
              bill_amount: journal.bill_amount || "0.00",
              paid_amount: journal.paid_amount || "0.00",
              amount_raw: journal.amount || "0.00",
            };
          });

          setTransactions(transformedTransactions);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to fetch customer data");
        setCustomer(null);
        setTransactions([]);
      } finally {
        setLoading(false);
        setDataLoaded(true);
      }
    };

    fetchData();
  }, [id]);

  // Auto-open print dialog when all data is loaded and DOM is ready
  useEffect(() => {
    if (customer && !loading && dataLoaded && !hasPrintedRef.current) {
      // Wait for DOM to be fully rendered, especially for large transaction lists
      // Use requestAnimationFrame to ensure rendering is complete
      const triggerPrint = () => {
        // Double requestAnimationFrame to ensure all rendering is complete
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Additional delay to ensure table is fully rendered
            setTimeout(() => {
              window.print();
              hasPrintedRef.current = true;
            }, 300);
          });
        });
      };

      // Small initial delay to ensure state updates are applied
      const timer = setTimeout(triggerPrint, 100);

      return () => clearTimeout(timer);
    }
  }, [customer, loading, dataLoaded]);

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate(`/customer/${customer?.id || ""}`);
  };

  // Show loading state
  if (loading) {
    return (
      <div className="print-invoice-container">
        <div className="print-invoice-header">
          <button className={`${isPartsURL ? "print-back-btn-parts" : isAccessoriesURL ? "print-back-btn-acc" : "print-back-btn"}`} onClick={handleBack}>
            <ArrowLeft size={15} />
            Back to Customer Details
          </button>
        </div>
        <div className="loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p style={{ marginTop: "1rem" }}>Loading customer data...</p>
        </div>
      </div>
    );
  }

  // Show empty state if no customer or transactions
  if (!customer || transactions.length === 0) {
    return (
      <div className="print-invoice-container">
        <div className="print-invoice-header">
          <button className={`${isPartsURL ? "print-back-btn-parts" : isAccessoriesURL ? "print-back-btn-acc" : "print-back-btn"}`} onClick={handleBack}>
            <ArrowLeft size={15} />
            Back to Customer Details
          </button>
        </div>
        <div className="loading-container">
          <p>
            No customer data available. Please export from the Customer Details
            page.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="print-invoice-container">
      {/* Header with Print button */}
      <div className="print-invoice-header">
        <div>
          <button className={`${isPartsURL ? "print-back-btn-parts" : isAccessoriesURL ? "print-back-btn-acc" : "print-back-btn"}`} onClick={handleBack}>
            <ArrowLeft size={15} />
            Back to Customer Details
          </button>
        </div>
        <div>
          <button className={`${isPartsURL ? "print-print-btn-parts" : isAccessoriesURL ? "print-print-btn-acc" : "print-print-btn"}`} onClick={handlePrint}>
            <Printer size={16} />
            Print
          </button>
        </div>
      </div>

      {/* Account Statement Content */}
      <div className={` ${isPartsURL ? "print-invoice-content-parts" : isAccessoriesURL ? "print-invoice-content-acc" : "print-invoice-content"}`}
      style={{ maxWidth: "860px" }}
      >
        {/* Header Section */}
        <div
          className="print-invoice-info"
          style={{
            borderBottom: "1px solid #EAEEF2",
            marginBottom: "20px",
            paddingBottom: "1px",
          }}
        >
          <div
            className="print-invoice-id"
            style={{
              borderBottom: "none",
              fontSize: "18px",
              fontWeight: "600",
            }}
          >
            Account Statement
          </div>
          <div
            className="print-invoice-date"
            style={{ fontSize: "14px", fontWeight: "5   00", color: "#424A53" }}
          >
            {formatDateTime()}
          </div>
        </div>

        {/* Customer Name and Account Balance */}
        <div
          className="print-invoice-info-name"
          style={{ marginBottom: "10px", borderBottom: "none" }}
        >
          <div
            className="print-customer-name"
            style={{ fontSize: "14px", fontWeight: "400", color: "#424A53" }}
          >
            Customer Name:{" "}
            <span style={{ fontWeight: "500" }}>{customer.name || "--"}</span>
          </div>
          <div
            style={{
              marginLeft: "auto",
              fontSize: "14px",
              fontWeight: "400",
              color: "#424A53",
            }}
          >
            Account Balance:{" "}
            <span
              className={`balance-badge ${
                currentBalance >= 0 ? "positive" : "negative"
              }`}
            >
              {formatBalance(currentBalance || 0)}
            </span>
          </div>
        </div>

        {/* Finance History Table */}
        <div className="customers-table-wrapper">
          <div className="customer-table-responsive">
            <table className="customer-table">
              <thead>
                <tr>
                  <th>DATE</th>
                  <th>INV/RTN/TRN ID</th>
                  <th>TYPE</th>
                  <th>DETAILS</th>
                  <th>CREDIT / DEBIT</th>
                  <th>BALANCE</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((transaction) => (
                  <tr key={transaction.id}>
                    <td>
                      {formatDate(transaction.tnx_date || transaction.date)}
                    </td>
                    <td>
                      {transaction.tnx_id || transaction.invoiceId || "--"}
                    </td>
                    <td>
                      <div className="customer-details-transaction-type">
                        {transaction.type === "Sale" && (
                          <>
                            <SaleIcon />
                            <span>Sale</span>
                          </>
                        )}
                        {transaction.type === "Return" && (
                          <>
                            <ReturnIcon />
                            <span>Return</span>
                          </>
                        )}
                        {transaction.type === "Credit" && (
                          <>
                            <CreditIcon />
                            <span>Credit</span>
                          </>
                        )}
                        {transaction.type === "Debit" && (
                          <>
                            <DebitIcon />
                            <span>Debit</span>
                          </>
                        )}
                      </div>
                    </td>
                    <td>
                      {transaction.type === "Sale" ? (
                        <div className="d-flex gap-5 flex-row">
                          <div className="d-flex flex-column">
                            <p
                              className="text-muted"
                              style={{ margin: 0, fontSize: "12px" }}
                            >
                              Bill Amount
                            </p>
                            <div
                              className="customer-details-amount"
                              style={{ fontSize: "14px" }}
                            >
                              €{transaction.billAmount?.toFixed(2) || "0.00"}
                            </div>
                          </div>
                          <div className="d-flex flex-column">
                            <p
                              className="text-muted"
                              style={{ margin: 0, fontSize: "12px" }}
                            >
                              Paid
                            </p>
                            <div
                              className="customer-details-amount positive"
                              style={{ fontSize: "14px" }}
                            >
                              €{transaction.paidAmount?.toFixed(2) || "0.00"}
                            </div>
                          </div>
                        </div>
                      ) : transaction.returnId ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "4px",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                color: "#57606A",
                                fontSize: "14px",
                              }}
                            >
                              Return Device
                            </span>
                            <span
                              style={{
                                color: "#57606A",
                                fontSize: "14px",
                                fontWeight: "500",
                              }}
                            >
                              €{transaction.bill_amount || transaction.billAmount?.toFixed(2) || "0.00"}
                            </span>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <span
                              style={{
                                color: "#57606A",
                                fontSize: "14px",
                              }}
                            >
                              Exchanged Device
                            </span>
                            <span
                              style={{
                                color: "#D800B0",
                                fontSize: "14px",
                                fontWeight: "500",
                              }}
                            >
                              €{transaction.paid_amount || transaction.paidAmount?.toFixed(2) || "0.00"}
                            </span>
                          </div>
                        </div>
                      ) : (
                        transaction.note || transaction.details || "--"
                      )}
                    </td>
                    <td>
                      {transaction.type === "Credit" ||
                      transaction.type === "Sale" ? (
                        <div
                          className="customer-details-amount negative"
                          style={{ fontSize: "14px", fontWeight: "500" }}
                        >
                          - €{Math.abs(transaction.amount || 0).toFixed(2)}
                        </div>
                      ) : (
                        <div
                          className="customer-details-amount positive"
                          style={{ fontSize: "14px", fontWeight: "500" }}
                        >
                          + €{Math.abs(transaction.amount || 0).toFixed(2)}
                        </div>
                      )}
                    </td>
                    <td>
                      <div
                        className={`balance-badge ${
                          (transaction.balance || 0) >= 0
                            ? "positive"
                            : "negative"
                        }`}
                      >
                        {formatBalance(transaction.balance || 0)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomerDetailsPrint;
